import type { SupabaseClient as Client } from '@supabase/supabase-js';
import type { SupabaseClient } from '../../lib/supabase';
import type { Database, Json } from '../../types/database.types';
import type { ScoreApi, ScoreField, ScoreLoad, ScoreSection, ScoreEntryLink } from './scoreboard';
import { editableField } from './scoreboard';

// Local, additive RPC contract until the approved batches are registered and
// database.types.ts is regenerated. Never hand-edit that generated file or
// create a second client: this is the existing signed-in session.
type ScoreDatabase = Omit<Database, 'public'> & {
  public: Omit<Database['public'], 'Functions'> & {
    Functions: Database['public']['Functions'] & {
      score_link_list: { Args: { p_section: string }; Returns: ScoreEntryLink[] };
      score_link_create: {
        Args: { p_section: string; p_person?: string };
        Returns: { id: string; token: string };
      };
      score_link_revoke: { Args: { p_link: string }; Returns: undefined };
      score_link_load: { Args: { p_token: string; p_date: string | null }; Returns: Json };
      score_link_board: { Args: { p_token: string; p_date: string | null }; Returns: Json };
      score_link_save: {
        Args: {
          p_token: string;
          p_date: string;
          p_version: number;
          p_fields_version: number;
          p_values: Json;
        };
        Returns: number;
      };
      score_totals: { Args: { p_date: string }; Returns: Json };
      score_load: { Args: { p_section: string; p_date: string }; Returns: Json };
      score_save: {
        Args: {
          p_section: string;
          p_date: string;
          p_version: number;
          p_fields_version: number;
          p_values: Json;
        };
        Returns: number;
      };
      score_edit_fields: {
        Args: { p_section: string; p_version: number; p_fields: Json };
        Returns: number;
      };
    };
  };
};
function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    throw new Error('Unexpected Scoreboard response. Reload before editing.');
  return value as Record<string, unknown>;
}
function field(value: unknown): ScoreField {
  const f = object(value);
  if (
    typeof f.id !== 'string' ||
    typeof f.label !== 'string' ||
    !['count', 'money', 'rate'].includes(String(f.value_type)) ||
    typeof f.is_active !== 'boolean' ||
    typeof f.position !== 'number'
  )
    throw new Error('Unexpected Scoreboard field.');
  return f as unknown as ScoreField;
}
export function readTotals(value: unknown): ScoreSection[] {
  if (!Array.isArray(value)) throw new Error('Unexpected Scoreboard response.');
  return value.map((entry) => {
    const s = object(entry);
    if (
      typeof s.id !== 'string' ||
      typeof s.name !== 'string' ||
      typeof s.can_edit !== 'boolean' ||
      !['daily', 'snapshot'].includes(String(s.kind)) ||
      !Array.isArray(s.metrics) ||
      typeof s.entered_fields !== 'number' ||
      typeof s.total_fields !== 'number'
    )
      throw new Error('Unexpected Scoreboard section.');
    for (const metric of s.metrics) {
      field(metric);
      object(object(metric).totals);
    }
    return s as unknown as ScoreSection;
  });
}
export function readLoad(value: unknown, section: string, date: string): ScoreLoad {
  const s = object(value);
  if (
    s.section_id !== section ||
    s.entry_date !== date ||
    !Number.isInteger(s.version) ||
    !Number.isInteger(s.fields_version) ||
    !Array.isArray(s.metrics)
  )
    throw new Error('Unexpected entry snapshot. Reload before editing.');
  s.metrics.forEach(field);
  return s as unknown as ScoreLoad;
}
export function createScoreApi(session: SupabaseClient): ScoreApi {
  const client = session as unknown as Client<ScoreDatabase>;
  return {
    async links(section) {
      const { data, error } = await client.rpc('score_link_list', { p_section: section });
      if (error) throw error;
      return data;
    },
    async createLink(section, person) {
      const { data, error } = await client.rpc(
        'score_link_create',
        person ? { p_section: section, p_person: person } : { p_section: section },
      );
      if (error) throw error;
      return data;
    },
    async revokeLink(id) {
      const { error } = await client.rpc('score_link_revoke', { p_link: id });
      if (error) throw error;
    },
    async totals(date) {
      const { data, error } = await client.rpc('score_totals', { p_date: date });
      if (error) throw error;
      return readTotals(data);
    },
    async load(section, date) {
      const { data, error } = await client.rpc('score_load', { p_section: section, p_date: date });
      if (error) throw error;
      return readLoad(data, section, date);
    },
    async save(snapshot, values) {
      const { data, error } = await client.rpc('score_save', {
        p_section: snapshot.section_id,
        p_date: snapshot.entry_date,
        p_version: snapshot.version,
        p_fields_version: snapshot.fields_version,
        p_values: values,
      });
      if (error) throw error;
      if (!Number.isInteger(data))
        throw new Error('Save result was not confirmed. Reload to check before trying again.');
      return data;
    },
    async editFields(snapshot, fields) {
      const { data, error } = await client.rpc('score_edit_fields', {
        p_section: snapshot.section_id,
        p_version: snapshot.fields_version,
        p_fields: fields.map(editableField) as unknown as Json,
      });
      if (error) throw error;
      if (!Number.isInteger(data))
        throw new Error('Field save was not confirmed. Reload to check before trying again.');
      return data;
    },
  };
}

export interface ScoreLinkSnapshot extends ScoreLoad {
  section_name: string;
}
/**
 * The read-only board an Ads entry link may see (score_link_board). Numbers
 * only: no ids, no names, no notes, no links. The reader below refuses any key
 * outside this list, so a server change that widened the payload would show
 * "board unavailable" instead of quietly rendering something new to an
 * outside freelancer.
 */
export interface ScoreLinkBoardMetric {
  label: string;
  value_type: 'count' | 'money' | 'rate';
  rate_format: 'percent' | 'money' | null;
  target: number | null;
  day: number | null;
  week: number | null;
  month: number | null;
}
export interface ScoreLinkBoardSection {
  name: string;
  kind: 'daily' | 'snapshot';
  metrics: ScoreLinkBoardMetric[];
}
export interface ScoreLinkBoard {
  entry_date: string;
  sections: ScoreLinkBoardSection[];
}
export const LINK_BOARD_KEYS = {
  board: ['entry_date', 'sections'],
  section: ['kind', 'metrics', 'name'],
  metric: ['day', 'label', 'month', 'rate_format', 'target', 'value_type', 'week'],
} as const;
function exactKeys(value: unknown, keys: readonly string[]): Record<string, unknown> {
  const o = object(value);
  const got = Object.keys(o).sort();
  if (got.length !== keys.length || got.some((key, i) => key !== keys[i]))
    throw new Error('Unexpected board response.');
  return o;
}
const numberOrNull = (v: unknown) => v === null || (typeof v === 'number' && Number.isFinite(v));
export function readLinkBoard(value: unknown, date: string): ScoreLinkBoard {
  const board = exactKeys(value, LINK_BOARD_KEYS.board);
  if (board.entry_date !== date || !Array.isArray(board.sections))
    throw new Error('Unexpected board response.');
  for (const entry of board.sections) {
    const section = exactKeys(entry, LINK_BOARD_KEYS.section);
    if (
      typeof section.name !== 'string' ||
      !['daily', 'snapshot'].includes(String(section.kind)) ||
      !Array.isArray(section.metrics)
    )
      throw new Error('Unexpected board section.');
    for (const item of section.metrics) {
      const m = exactKeys(item, LINK_BOARD_KEYS.metric);
      if (
        typeof m.label !== 'string' ||
        !['count', 'money', 'rate'].includes(String(m.value_type)) ||
        !(m.rate_format === null || m.rate_format === 'percent' || m.rate_format === 'money') ||
        ![m.target, m.day, m.week, m.month].every(numberOrNull)
      )
        throw new Error('Unexpected board metric.');
    }
  }
  return board as unknown as ScoreLinkBoard;
}

export interface ScoreLinkApi {
  load(this: void, token: string, date: string): Promise<ScoreLinkSnapshot>;
  board(this: void, token: string, date: string): Promise<ScoreLinkBoard>;
  save(
    this: void,
    token: string,
    snapshot: ScoreLinkSnapshot,
    values: Record<string, number | null>,
  ): Promise<number>;
}
export function createScoreLinkApi(session: SupabaseClient): ScoreLinkApi {
  const client = session as unknown as Client<ScoreDatabase>;
  return {
    async load(token, date) {
      const { data, error } = await client.rpc('score_link_load', { p_token: token, p_date: date });
      if (error) throw error;
      const result = object(data);
      if (typeof result.section_id !== 'string' || typeof result.section_name !== 'string')
        throw new Error('Invalid entry response');
      return { ...readLoad(data, result.section_id, date), section_name: result.section_name };
    },
    async board(token, date) {
      const { data, error } = await client.rpc('score_link_board', {
        p_token: token,
        p_date: date,
      });
      if (error) throw error;
      return readLinkBoard(data, date);
    },
    async save(token, snapshot, values) {
      const { data, error } = await client.rpc('score_link_save', {
        p_token: token,
        p_date: snapshot.entry_date,
        p_version: snapshot.version,
        p_fields_version: snapshot.fields_version,
        p_values: values,
      });
      if (error) throw error;
      if (!Number.isInteger(data)) throw new Error('Save was not confirmed. Reload to check.');
      return data;
    },
  };
}
